import type {
  TeacherBatch,
  TeacherMaterial,
  TeacherProfile,
  TeacherQuestion,
  TeacherStudent,
} from "./types";

const NODE_API = "https://node-api.mediitacademy.com/api";
export const TEACHER_API_BASE = "https://node-api.mediitacademy.com/api/teachers";
// export const TEACHER_API_BASE = "http://localhost:9090/api/teachers";

export const getTeacherAuth = () => {
  const raw = localStorage.getItem("mediit_admin_auth");
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getTeacherToken = () => {
  const auth = getTeacherAuth();
  return auth?.token || localStorage.getItem("adminToken") || localStorage.getItem("token") || "";
};

export const getTeacherHeaders = (): Record<string, string> => {
  const token = getTeacherToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const parseJson = async (response: Response) => {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || data?.error || data?.details || "Request failed");
  }
  return data;
};

export const teacherApi = {
  async me(): Promise<TeacherProfile> {
    const response = await fetch(`${TEACHER_API_BASE}/me`, {
      headers: getTeacherHeaders(),
    });
    const data = await parseJson(response);
    return data.teacher || data;
  },

  async myBatches(): Promise<TeacherBatch[]> {
    const response = await fetch(`${TEACHER_API_BASE}/me/batches`, {
      headers: getTeacherHeaders(),
    });
    const data = await parseJson(response);
    return Array.isArray(data) ? data : data.batches || [];
  },

  async myStudents(): Promise<TeacherStudent[]> {
    const response = await fetch(`${TEACHER_API_BASE}/me/students`, {
      headers: getTeacherHeaders(),
    });
    const data = await parseJson(response);
    return Array.isArray(data) ? data : data.students || [];
  },

  async myMaterials(): Promise<TeacherMaterial[]> {
    const response = await fetch(`${TEACHER_API_BASE}/me/materials`, {
      headers: getTeacherHeaders(),
    });
    const data = await parseJson(response);
    return Array.isArray(data) ? data : data.materials || [];
  },

  async createMaterial(payload: {
    title: string;
    type: string;
    courseId?: number | string;
    batchId?: number | string;
    externalUrl?: string;
    status?: string;
  }): Promise<void> {
    const response = await fetch(`${TEACHER_API_BASE}/me/materials`, {
      method: "POST",
      headers: getTeacherHeaders(),
      body: JSON.stringify(payload),
    });
    await parseJson(response);
  },

  async myQuestions(): Promise<TeacherQuestion[]> {
    const response = await fetch(`${TEACHER_API_BASE}/me/questions`, {
      headers: getTeacherHeaders(),
    });
    const data = await parseJson(response);
    return Array.isArray(data) ? data : data.questions || [];
  },

  async createQuestion(payload: {
    question: string;
    subject?: string;
    chapter?: string;
    difficulty?: string;
    answer?: string;
    explanation?: string;
    status?: string;
  }): Promise<void> {
    const response = await fetch(`${TEACHER_API_BASE}/me/questions`, {
      method: "POST",
      headers: getTeacherHeaders(),
      body: JSON.stringify(payload),
    });
    await parseJson(response);
  },
};

export const formatTeacherDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatTeacherTime = (value?: string | null) => {
  if (!value) return "-";
  const time = String(value).slice(0, 5);
  const [hourText, minute] = time.split(":");
  const hour = Number(hourText);
  if (Number.isNaN(hour)) return value;
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute || "00"} ${suffix}`;
};